import { getAuthStore } from '@/store/authStore';

export interface GeneratedFileSyncRequest {
  url: string;
  authorization?: string;
  filename: string;
  relativePath?: string;
  accountName?: string;
  spaceName?: string;
  spaceId?: string;
  projectName?: string;
  projectId?: string;
  taskId?: string;
  userId?: string | number;
}

export interface GeneratedFileSyncResult {
  success: boolean;
  filePath?: string;
  error?: string;
}

export interface GeneratedFileSyncOptions {
  accountName?: string;
  spaceName?: string;
  spaceId?: string;
  projectName?: string;
  projectId?: string;
  taskId?: string | null;
  userId?: string | number | null;
  electronAPI?: {
    saveGeneratedRemoteFile?: (
      request: GeneratedFileSyncRequest
    ) => Promise<GeneratedFileSyncResult>;
  } | null;
}

function isHttpUrl(value: string): boolean {
  return /^https?:\/\//i.test(value);
}

function taskDirName(taskId?: string | null): string | undefined {
  const trimmed = taskId?.trim();
  if (!trimmed) {
    return undefined;
  }
  return `task_${trimmed.replace(/^task_/i, '')}`;
}

function ensureTaskScopedRelativePath(
  relativePath?: string,
  taskId?: string | null
): string | undefined {
  const normalized = relativePath?.replace(/\\/g, '/').replace(/^\/+/, '');
  if (!normalized) {
    return undefined;
  }
  if (/^task_[^/]+(?:\/|$)/i.test(normalized)) {
    return normalized;
  }
  const taskDir = taskDirName(taskId);
  return taskDir ? `${taskDir}/${normalized}` : normalized;
}

function addGeneratedFileStreamContext(
  url: string,
  relativePath: string | undefined,
  options: Omit<GeneratedFileSyncOptions, 'electronAPI'>,
  auth: ReturnType<typeof getAuthStore>
): string {
  try {
    const parsed = new URL(url);
    if (!parsed.pathname.includes('/files/stream')) {
      return url;
    }

    const scopedRelativePath = ensureTaskScopedRelativePath(
      parsed.searchParams.get('path') || relativePath,
      options.taskId
    );
    const projectId = options.projectId?.trim();
    const accountName = options.accountName || auth.email || undefined;
    const spaceId = options.spaceId?.trim();
    const userId =
      options.userId === null || options.userId === undefined
        ? auth.user_id
        : options.userId;

    if (scopedRelativePath) {
      parsed.searchParams.set('path', scopedRelativePath);
    }
    if (projectId && !parsed.searchParams.get('project_id')) {
      parsed.searchParams.set('project_id', projectId);
    }
    if (accountName && !parsed.searchParams.get('email')) {
      parsed.searchParams.set('email', accountName);
    }
    if (spaceId && !parsed.searchParams.get('space_id')) {
      parsed.searchParams.set('space_id', spaceId);
    }
    if (
      userId !== null &&
      userId !== undefined &&
      String(userId).trim() &&
      !parsed.searchParams.get('user_id')
    ) {
      parsed.searchParams.set('user_id', String(userId));
    }

    return parsed.toString();
  } catch {
    return url;
  }
}

export function buildGeneratedFileSyncRequest(
  file: FileInfo,
  options: Omit<GeneratedFileSyncOptions, 'electronAPI'> = {}
): GeneratedFileSyncRequest | null {
  const url = file.path?.trim();
  if (!file.isRemote || !url || !isHttpUrl(url)) {
    return null;
  }

  const auth = getAuthStore();
  const token = auth.token;
  const augmentedUrl = addGeneratedFileStreamContext(
    url,
    file.relativePath,
    options,
    auth
  );
  const userId =
    options.userId === null || options.userId === undefined
      ? auth.user_id
      : options.userId;
  const filename =
    file.name || file.relativePath?.split(/[\\/]/).pop() || 'download';

  return {
    url: augmentedUrl,
    authorization: token ? `Bearer ${token}` : undefined,
    filename,
    relativePath: file.relativePath,
    accountName: options.accountName || auth.email || undefined,
    spaceName: options.spaceName,
    spaceId: options.spaceId,
    projectName: options.projectName,
    projectId: options.projectId,
    ...(options.taskId ? { taskId: options.taskId } : {}),
    ...(userId === null || userId === undefined || !String(userId).trim()
      ? {}
      : { userId }),
  };
}

export async function localizeGeneratedFile(
  file: FileInfo,
  options: GeneratedFileSyncOptions = {}
): Promise<FileInfo> {
  const request = buildGeneratedFileSyncRequest(file, options);
  const saveGeneratedRemoteFile = options.electronAPI?.saveGeneratedRemoteFile;
  if (!request || !saveGeneratedRemoteFile) {
    return file;
  }

  try {
    const result = await saveGeneratedRemoteFile(request);
    if (!result?.success || !result.filePath) {
      console.warn(
        'Failed to sync generated file locally:',
        result?.error || 'Unknown error'
      );
      return file;
    }

    return {
      ...file,
      path: result.filePath,
      isRemote: false,
      localPathAvailable: true,
      supportsRanges: undefined,
      remotePath: file.path,
    } as FileInfo & { remotePath: string };
  } catch (error) {
    console.warn('Failed to sync generated file locally:', error);
    return file;
  }
}
